import React from 'react';
import { BaseModal } from './BaseModal';
import type { BaseModalProps } from './BaseModal';
import { Button } from '../Button';
import { 
  HiExclamationTriangle, 
  HiInformationCircle, 
  HiCheckCircle,
  HiXCircle
} from 'react-icons/hi2';

export interface ConfirmModalProps extends Omit<BaseModalProps, 'children'> {
  type?: 'info' | 'warning' | 'danger' | 'success';
  message: string;
  details?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  loading?: boolean;
  disabled?: boolean;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  type = 'info',
  title,
  message,
  details,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  onConfirm,
  onCancel,
  loading = false,
  disabled = false,
  isOpen,
  onClose,
  ...props
}) => {
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onClose();
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const getIcon = () => {
    switch (type) {
      case 'warning':
        return <HiExclamationTriangle className="w-6 h-6 text-yellow-500" />;
      case 'danger':
        return <HiXCircle className="w-6 h-6 text-red-500" />;
      case 'success':
        return <HiCheckCircle className="w-6 h-6 text-green-500" />;
      default:
        return <HiInformationCircle className="w-6 h-6 text-blue-500" />;
    }
  };

  const getConfirmButtonVariant = () => {
    switch (type) {
      case 'danger':
        return 'destructive';
      case 'warning':
        return 'secondary';
      case 'success':
        return 'primary';
      default:
        return 'primary';
    }
  };

  const getDefaultTitle = () => {
    switch (type) {
      case 'warning':
        return 'Confirmar Ação';
      case 'danger':
        return 'Ação Irreversível';
      case 'success':
        return 'Confirmar Operação';
      default:
        return 'Confirmação Necessária';
    }
  };

  const getDescription = () => {
    switch (type) {
      case 'warning':
        return 'Esta ação requer sua confirmação antes de prosseguir.';
      case 'danger':
        return 'Atenção: Esta ação não pode ser desfeita.';
      case 'success':
        return 'Confirme para completar esta operação.';
      default:
        return 'Por favor, confirme se deseja continuar.';
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={title || getDefaultTitle()}
      description={details || getDescription()}
      icon={getIcon()}
      useStandardLayout={true}
      size="sm"
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
      {...props}
    >
      <div className="p-4">
        {/* Message */}
        <div className="text-center mb-4">
          <p className="text-sm text-gray-900 dark:text-gray-100 leading-relaxed">
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={loading}
            className="min-w-20"
          >
            {cancelText}
          </Button>
          
          <Button
            variant={getConfirmButtonVariant()}
            size="sm"
            onClick={handleConfirm}
            disabled={disabled}
            isLoading={loading}
            className="min-w-20"
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};
